import { del, get, post, postBlob, put } from '@/shared/utils/FetchV2.util';
import type {
  CatalogRetentionConceptInput,
  CatalogRetentionResult,
  Receiver,
  SaveReceiverPayload,
  UserInvoicingDataResponse,
} from '@/shared/services/invoice.service';
import type { IvaTreatment } from '../../constants/ivaTreatment';
import type { CancelInvoiceOutcome } from '../../lib/cancellation';
import { fiscalEngineOptions } from '../../services/fiscalEngineLocal';
import type { CancelReasonCode } from '../lib/cancelReasons';
import type { InvoiceQuota } from '../lib/quota';

/**
 * Las llamadas que la facturación nueva compartía con `/invoice`, solo contra
 * `heru-fiscal-engine`. Mismo sub-path y misma forma que tenían en
 * `heru-core-invoice-service`, sin respaldo hacia él: el legacy conserva las
 * suyas en `shared/services/invoice.service.ts` y no cambia.
 *
 * Como el resto de los servicios Go de facturación, un 401 no borra la sesión:
 * el fiscal-engine local firma con otra llave que el gateway de prod.
 */
const fiscalEngine = (endpoint: string) => ({
  service: 'fiscalEngine' as const,
  endpoint,
  options: fiscalEngineOptions(endpoint),
  skipAccountError: true,
});

const RECEIVERS_ENDPOINT = '/v2/invoice-receivers/users';

/** La libreta de clientes en caché: la leen el inicio, el cambio de receptor y el complemento de pago. */
export const RECEIVERS_QUERY_KEY = ['invoicing', 'my-receivers'];

/** Nombre, RFC, CP, estado del sello y regímenes del emisor, por el `user_id` del token y sin pasar por el vínculo con el SAT. */
export const getInvoicingDataService = (): Promise<UserInvoicingDataResponse> =>
  get<UserInvoicingDataResponse>(fiscalEngine('/operations/user'));

/** El desglose del catálogo revisado de retenciones; no timbra ni persiste. */
export const previewRetentionsService = (
  issuer: { tax_regime: string; rfc?: string },
  receiver: { rfc: string },
  concepts: CatalogRetentionConceptInput[],
): Promise<CatalogRetentionResult> =>
  post<CatalogRetentionResult>({
    ...fiscalEngine('/invoices/retentions/preview'),
    body: { issuer, receiver, concepts },
  });

/** The current quota: read by the wizard paywall, the plan chip of the home and the plans screen. */
export const QUOTA_QUERY_KEY = ['invoicing', 'quota'];

/** Plan efectivo y uso de la ventana del mes, por el `user_id` del token. */
export const getQuotaService = (): Promise<InvoiceQuota> => get<InvoiceQuota>(fiscalEngine('/cfdi/cupo'));

export const getReceiversService = (): Promise<{ receivers: Receiver[] }> =>
  get<{ receivers: Receiver[] }>(fiscalEngine(RECEIVERS_ENDPOINT));

/** Un concepto de la última factura a un cliente, tal como salió timbrado. */
export interface LastInvoiceConcept {
  product_code: string;
  unit_code: string;
  /** La unidad del XML; vacía en lo que timbró el Go (se resuelve del catálogo). */
  unit_name?: string;
  description: string;
  quantity: number;
  unit_price: number;
  discount: number;
  /** Importe: cantidad × valor unitario, antes de descuento e impuestos. */
  amount: number;
  /** Vacío cuando el borrador de la v2 no lo puede expresar. */
  iva_treatment?: IvaTreatment;
}

/**
 * La factura de ingreso vigente más reciente del usuario a un cliente de su libreta, o a
 * un receptor genérico (Público en general / en el extranjero), que no vive en la libreta.
 */
export interface ClientLastInvoice {
  /** Id de la libreta; ausente en un receptor genérico. */
  receiver_id?: number;
  /** Modo del receptor genérico; ausente en un cliente de la libreta. */
  generic_mode?: 'publico_general' | 'publico_extranjero';
  source: 'fiscal-engine' | 'nestjs';
  issued_at: string;
  total: number;
  currency: string;
  payment_method: string;
  payment_form: string;
  uso_cfdi?: string;
  /** `false` si lleva algo que el borrador no expresa (IEPS, IVA por cuota, otra moneda). */
  repeatable: boolean;
  not_repeatable_reason?: string;
  concepts: LastInvoiceConcept[];
}

export const CLIENT_LAST_INVOICES_QUERY_KEY = ['invoicing', 'client-last-invoices'];

/**
 * La última factura a cada cliente, para «Repetir última factura» en los recientes del
 * inicio y en los receptores genéricos de «Más usado».
 */
export const getClientLastInvoicesService = (
  receiverIds: number[],
  generic: ReadonlyArray<NonNullable<ClientLastInvoice['generic_mode']>> = [],
): Promise<{ invoices: ClientLastInvoice[] }> => {
  const params = [
    ...(receiverIds.length ? [`receiver_ids=${receiverIds.join(',')}`] : []),
    ...(generic.length ? [`generic=${generic.join(',')}`] : []),
  ];
  return get<{ invoices: ClientLastInvoice[] }>(
    fiscalEngine(`/cfdi/clientes/ultimas-facturas?${params.join('&')}`),
  );
};

/** Un cliente al que el usuario ya le facturó fuera de heru, según su CFDI más reciente. */
export interface SuggestedReceiver {
  rfc: string;
  name: string;
  tax_regime: string | null;
  postal_code: string | null;
  usocfdi: string | null;
  /** Campos que el CFDI de origen no trae (un 3.3 no tiene régimen ni CP del receptor). */
  missing: Array<'tax_regime' | 'postal_code' | 'usocfdi'>;
  cfdi_version: string;
  /** AAAA-MM-DD, fecha del CFDI de origen. */
  last_invoiced_at: string;
  source_uuid: string;
  source: 'financials';
  in_address_book: boolean;
  address_book_id: number | null;
}

export const SUGGESTED_RECEIVERS_QUERY_KEY = ['invoicing', 'v2', 'suggested-receivers'];

/** Los clientes de sus CFDI emitidos (financials_service), solo lectura. */
export const getSuggestedReceiversService = (): Promise<{ receivers: SuggestedReceiver[] }> =>
  get<{ receivers: SuggestedReceiver[] }>(fiscalEngine('/cfdi/receptores/sugeridos'));

/**
 * Un error del Go siempre lanza: no regresa el cuerpo del error como si fuera respuesta.
 * `id` es el del receptor en la libreta (`public.receiver.id`), que puede ser una fila
 * que ya existía: la identidad es global y lo que se crea es el vínculo.
 */
export const saveReceiverService = (
  payload: SaveReceiverPayload,
): Promise<{ message: string; id: number }> =>
  post<{ message: string; id: number }>({ ...fiscalEngine(RECEIVERS_ENDPOINT), body: payload });

interface GoCancellation {
  document_status: CancelInvoiceOutcome['documentStatus'];
  cancellation_status: CancelInvoiceOutcome['cancellationStatus'];
}

/**
 * Pide la cancelación al Go y a nadie más. Con lo que el Go todavía no puede
 * cancelar responde 404 `CFDI_DOCUMENTO_NO_ENCONTRADO`, y eso sale como error:
 * repetir el trámite en otro motor no es opción. El motivo va sin default.
 */
export const cancelInvoiceService = async (
  invoiceUuid: string,
  reason: CancelReasonCode,
): Promise<CancelInvoiceOutcome> => {
  const result = await del<GoCancellation>({
    ...fiscalEngine(`/cfdi/${invoiceUuid}/cancel`),
    body: { reason },
  });
  return {
    engine: 'go',
    documentStatus: result.document_status,
    cancellationStatus: result.cancellation_status,
  };
};

/** El logo de la marca del PDF, tal como quedó registrado (`pdfbrand.logo`). */
export interface PdfBrandLogo {
  /** sha256 del archivo: el mismo logo subido dos veces es el mismo id. */
  logo_id: string;
  content_type: 'image/png' | 'image/jpeg';
  width: number;
  height: number;
  size_bytes: number;
  /** URL firmada de 15 minutos. Vacía si la firma falló. */
  url: string;
  url_expires_at: string;
}

/** Los límites de la marca los publica el servidor: la web no los repite. */
export interface PdfBrandLimits {
  title_max_chars: number;
  description_max_chars: number;
  logo_max_bytes: number;
  logo_types: string[];
  logo_min_side_px: number;
  logo_max_side_px: number;
  logo_max_aspect_ratio: number;
  /** Of the email message; optional while the backend does not publish them. */
  email_message_max_chars?: number;
  email_message_max_lines?: number;
  /** Of the reply-to address; absent on a backend without it, and then the field is hidden. */
  reply_to_email_max_chars?: number;
}

/**
 * La marca del PDF de las facturas que emite el usuario. Sin marca guardada
 * (`is_custom: false`) trae los valores de heru y sin logo: así arranca el formulario.
 * `effective_colors` son los que el PDF de verdad pinta (el renderer oscurece los
 * colores claros); `attribution` es el pie fijo, que no se edita.
 */
export interface PdfBrandView {
  is_custom: boolean;
  brand: {
    title: string;
    description: string;
    primary_color: string;
    secondary_color: string;
    logo: PdfBrandLogo | null;
    /** The message of the email their clients receive; empty without one. */
    email_message?: string;
    /** Where the client's replies to that email go (its Reply-To); empty without one. */
    reply_to_email?: string;
  };
  effective_colors: { primary: string; secondary: string; adjusted: boolean };
  attribution: string;
  updated_at: string | null;
  limits: PdfBrandLimits;
  /**
   * `true` when this deployment sends the client's copy of the invoice email with the
   * brand (it has the branded template). Whether THIS user's does is the `email_brand`
   * feature of their plan. Absent or `false`: the screen does not ask for the message.
   */
  email_enabled?: boolean;
}

/**
 * Cuerpo de `PUT /cfdi/pdf-brand` y de su vista previa: la marca COMPLETA. Huma
 * rechaza con 422 un campo de más, así que van exactamente estos.
 */
export interface PdfBrandFields {
  title: string;
  description: string;
  primary_color: string;
  secondary_color: string;
  /** `null` = sin logo en el encabezado. */
  logo_id: string | null;
  /**
   * Only when the email card is shown: a backend that does not know it answers 422.
   * Absent keeps the saved one; empty removes it.
   */
  email_message?: string;
  /** Same rules as `email_message`, and only when the server publishes its limit. */
  reply_to_email?: string;
}

const PDF_BRAND_ENDPOINT = '/cfdi/pdf-brand';

export const PDF_BRAND_QUERY_KEY = ['invoicing', 'v2', 'pdf-brand'];

/** Fuera de `SERVICE_PDF_BRANDING_USERS` responde 404: la funcionalidad no existe para esa cuenta. */
export const getPdfBrandService = (): Promise<PdfBrandView> =>
  get<PdfBrandView>(fiscalEngine(PDF_BRAND_ENDPOINT));

/** Reemplaza la marca completa. Aplica a los PDF que se pinten después, nunca a los ya emitidos. */
export const savePdfBrandService = (fields: PdfBrandFields): Promise<PdfBrandView> =>
  put<PdfBrandView>({ ...fiscalEngine(PDF_BRAND_ENDPOINT), body: fields });

/** Vuelve a la marca de heru. Idempotente. */
export const resetPdfBrandService = (): Promise<PdfBrandView> =>
  del<PdfBrandView>(fiscalEngine(PDF_BRAND_ENDPOINT));

/** Registra un logo SIN cambiar la marca guardada: entra a la marca hasta el siguiente `PUT`. */
export const uploadPdfBrandLogoService = (fileBase64: string): Promise<{ logo: PdfBrandLogo }> =>
  post<{ logo: PdfBrandLogo }>({
    ...fiscalEngine(`${PDF_BRAND_ENDPOINT}/logo`),
    body: { file_base64: fileBase64 },
  });

/** PDF de una factura de muestra con la marca borrador. No persiste nada; un error llega como `Blob`. */
export const previewPdfBrandService = (fields: PdfBrandFields): Promise<Blob> =>
  postBlob({ ...fiscalEngine(`${PDF_BRAND_ENDPOINT}/preview`), body: fields });
